import { z } from "zod";

import {
  ChatListLastMessageRoleSchema,
  ChatListSharedFiltersSchema
} from "../chat/list-filters.schema";

/**
 * Per-listener configuration for the proactive `conversation.idle` Platform
 * Listener event. Lives inside `PlatformListenerTriggerNodeData.idleConfig`
 * and is persisted on the `Trigger.config` JSON blob.
 *
 * The polling job (BullMQ cron, key `PLATFORM_LISTENER_IDLE_SCAN`) reads this
 * block at scan time and applies:
 *   1. `thresholdMinutes` → `lastMessageUpdatedAt < NOW() - threshold`
 *   2. `lastMessageBy`    → join through `Chat.lastMessage` and filter by
 *                            message role (USER / AGENT / SYSTEM). Undefined
 *                            means ANY role qualifies.
 *   3. `filters`          → the shared chat-list filter set
 *                            (ticketStatus, lastMessageBefore/After,
 *                            lastMessageTypeIn, commentContains/NotContains).
 *
 * Validation is the single source of truth — V1 REST, workflow `inbox.list`
 * action, and this trigger all funnel through `ChatListSharedFiltersSchema`
 * so the same superRefine conflict checks apply uniformly.
 */
/**
 * Floor on the idle threshold. Set to 1 minute — matches the cron tick
 * cadence, the smallest value the scanner can meaningfully resolve as
 * "older than threshold". Operators can re-tune the cron via the
 * SystemConfig admin panel; this floor doesn't move with it because the
 * preview UI and the inline error copy already explain the relationship.
 *
 * The threshold value does NOT control scan frequency: it only changes which
 * chats are eligible per scan, and the per-trigger candidate-set is bounded
 * by `CANDIDATE_LIMIT` (2000) + `FIRE_CAP` (500) in the scanner regardless
 * of how many chats cross the threshold. So a low threshold cannot cause
 * the self-DDoS the previous floor was guarding against; it just means
 * "any idle conversation" instead of "very-idle conversation".
 *
 * Zero is still forbidden — a `lastMessageUpdatedAt < NOW()` predicate at
 * the boundary would match every live chat every cycle, which is the
 * actual abusive case.
 */
export const IDLE_THRESHOLD_MIN_MINUTES = 1 as const;
/**
 * Ceiling on the idle threshold — 10 years (5,256,000 minutes). The scanner
 * computes `new Date(Date.now() - thresholdMinutes * 60_000)`; without an
 * upper bound, a user typo (e.g. typing "100000100000" by accident in the
 * Minutes/Hours/Days picker) overflows JS's ±8.64×10^15 ms Date range and
 * Prisma returns 500 on an Invalid Date in the `where` clause. 10 years is
 * generous enough that no legitimate threshold lands near it, and any value
 * above the cap is almost certainly an input mistake worth surfacing as a
 * clear 400 rather than an opaque 500.
 */
export const IDLE_THRESHOLD_MAX_MINUTES = 60 * 24 * 365 * 10;

export const PlatformListenerIdleConfigSchema = z.object({
  /**
   * Idle threshold in MINUTES. Polling fires when the chat's
   * `lastMessageUpdatedAt` is older than this. Stored as minutes — matches
   * the cron tick granularity so the scanner can resolve every threshold
   * down to the floor. See `IDLE_THRESHOLD_MIN_MINUTES` /
   * `IDLE_THRESHOLD_MAX_MINUTES` for bounds.
   */
  thresholdMinutes: z
    .number()
    .int()
    .min(IDLE_THRESHOLD_MIN_MINUTES)
    .max(IDLE_THRESHOLD_MAX_MINUTES),
  /**
   * Optional: only fire if the most recent message role matches this value.
   * Undefined means the role does not constrain the match.
   */
  lastMessageBy: ChatListLastMessageRoleSchema.optional(),
  /**
   * Optional shared chat-list filters. Re-uses the canonical schema so the
   * conflict refinements (ticketStatus / ticketStatusIn / ticketStatusNot
   * mutual exclusion, lastMessageBefore > lastMessageAfter) fire here too.
   */
  filters: ChatListSharedFiltersSchema.optional()
});

export type PlatformListenerIdleConfig = z.infer<typeof PlatformListenerIdleConfigSchema>;
